import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchTamilValues } from "../../redux/dropdownoptionsSlice";
import { fetchPayPurpose } from "../../redux/paypurposeSlice";
import { normalizeReceiptNoForDisplay } from "../../utils/receiptNoFormatter";
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit,
  Delete,
  History,
  AddBox,
} from "@mui/icons-material";

const Row = ({
  donor,
  allDonations,
  payPurposes,
  donationCount,
  onEdit,
  onDelete,
  onView,
  onAppend,
  role,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const donorDonations = useMemo(() => {
    if (!Array.isArray(allDonations)) return [];

    return allDonations
      .map((donation) => {
        const payPurposeData =
          payPurposes.find(
            (p) =>
              p.donorId === donation.donorId &&
              p.transactionId === donation.transactionId
          ) || {};

        return {
          ...donation,
          // receiptNo is stored with a year prefix for uniqueness (e.g.
          // "202635119") on newer donations but not on older/imported ones
          // (e.g. "35119") -- always show the human-facing number, never
          // the internal storage format.
          displayReceiptNo: normalizeReceiptNoForDisplay(
            donation.receiptNo,
            donation.receiptDate
          ),
          tributeName: payPurposeData.tributeName ?? donation.tributeName ?? "",
          sharma: payPurposeData.sharma ?? donation.sharma ?? "",
          poojaDate: payPurposeData.poojaDate ?? donation.poojaDate ?? "",
          tamilMonths: payPurposeData.tamilMonths ?? donation.tamilMonths ?? "",
          nachathiram: payPurposeData.nachathiram ?? donation.nachathiram ?? "",
          thithi: payPurposeData.thithi ?? donation.thithi ?? "",
          utsavam: payPurposeData.utsavam ?? donation.utsavam ?? "",
          complete: payPurposeData.complete ?? donation.complete ?? false,
        };
      })
      .sort((a, b) => {
        // Sort by receipt number ascending (using the normalized,
        // un-prefixed value -- receiptNo is a string, so a plain string
        // comparison would sort "10" before "9" -- compare as numbers).
        const receiptA = Number(a.displayReceiptNo) || 0;
        const receiptB = Number(b.displayReceiptNo) || 0;
        return receiptA - receiptB;
      });
  }, [allDonations, payPurposes]);

  const renderActionButtons = (donation) => (
    <Box display="flex">
      {(role === "admin1" ||
        role === "admin2" ||
        role === "editor2" ||
        role === "superadmin") && (
        <IconButton
          onClick={() => onEdit(donation)}
          title="Edit"
          style={{ color: "#1976d2" }}
          data-log="Edit Icon Button"
        >
          <Edit />
        </IconButton>
      )}
      {(role === "superadmin" || role === "admin1" || role === "admin2") && (
        <IconButton
          onClick={() => onDelete(donation)}
          title="Delete"
          style={{ color: "#d32f2f" }}
          data-log="Delete Icon Button"
        >
          <Delete />
        </IconButton>
      )}
      {(role === "admin1" || role === "admin2" || role === "superadmin") && (
        <IconButton
          onClick={() => onView(donation)}
          title="View History"
          style={{ color: "#1976D2" }}
          data-log="View History Icon Button"
        >
          <History />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: "#f9f9f9",
          "&:hover": { backgroundColor: "#f1f1f1" },
        }}
      >
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          <IconButton size="small" data-log="Donation Table Expand Icon Button">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          {" "}
          {(role === "admin1" ||
            role === "admin2" ||
            role === "superadmin" ||
            role === "editor2") && (
            <IconButton
              onClick={() => onAppend(donor)}
              title="Add Donation"
              style={{ color: "#1976d2" }}
              data-log="Add Donation Icon Button"
            >
              <AddBox />
            </IconButton>
          )}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          {donor.donorId}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          {donor.firstName}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          {donor.mobile1 || "-"}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          {donor.mobile2 || "-"}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          {donor.city}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={7}
          sx={{
            p: 0,
            border: 0,
            backgroundColor: "#fafafa",
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                borderTop: "2px solid #ddd",
                pt: 1,
                pb: 1,
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": {
                  height: "10px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#e0e0e0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#1976d2",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                },
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 1600,
                  width: "max-content",
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap", fontWeight: "bold", position: "sticky", left: 0, backgroundColor: "#e3f2fd", zIndex: 2 }}>Actions</TableCell>
                    <TableCell sx={{ width: 130, whiteSpace: "nowrap", fontWeight: "bold" }}>Transaction ID</TableCell>
                    <TableCell sx={{ width: 90, whiteSpace: "nowrap", fontWeight: "bold" }}>Book No</TableCell>
                    <TableCell sx={{ width: 110, whiteSpace: "nowrap", fontWeight: "bold" }}>Receipt No</TableCell>
                    <TableCell sx={{ width: 110, whiteSpace: "nowrap", fontWeight: "bold" }}>Actual Receipt No</TableCell>
                    <TableCell sx={{ width: 130, whiteSpace: "nowrap", fontWeight: "bold" }}>Receipt Date</TableCell>
                    <TableCell sx={{ width: 100, whiteSpace: "nowrap", fontWeight: "bold" }}>Amount</TableCell>
                    <TableCell sx={{ width: 110, whiteSpace: "nowrap", fontWeight: "bold" }}>Pay Mode</TableCell>
                    <TableCell sx={{ width: 150, whiteSpace: "nowrap", fontWeight: "bold" }}>Bank Name</TableCell>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap", fontWeight: "bold" }}>Instrument No</TableCell>
                    <TableCell sx={{ width: 150, whiteSpace: "nowrap", fontWeight: "bold" }}>Instrument Date</TableCell>
                    <TableCell sx={{ width: 160, whiteSpace: "nowrap", fontWeight: "bold" }}>UPI Tnx ID</TableCell>
                    <TableCell sx={{ width: 130, whiteSpace: "nowrap", fontWeight: "bold" }}>Pay Purpose</TableCell>
                    <TableCell sx={{ width: 180, whiteSpace: "nowrap", fontWeight: "bold" }}>Tribute Name</TableCell>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap", fontWeight: "bold" }}>Sharma</TableCell>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap", fontWeight: "bold" }}>Pooja Date</TableCell>
                    <TableCell sx={{ width: 160, whiteSpace: "nowrap", fontWeight: "bold" }}>Tamil Months</TableCell>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap", fontWeight: "bold" }}>Nachathiram</TableCell>
                    <TableCell sx={{ width: 120, whiteSpace: "nowrap", fontWeight: "bold" }}>Thithi</TableCell>
                    <TableCell sx={{ width: 120, whiteSpace: "nowrap", fontWeight: "bold" }}>Utsavam</TableCell>
                    <TableCell sx={{ width: 180, whiteSpace: "nowrap", fontWeight: "bold" }}>Donation Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {donorDonations.map((donation) => (
                    <TableRow
                      key={donation._id}
                      sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <TableCell
                        sx={{
                          width: 140,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        {renderActionButtons(donation)}
                      </TableCell>
                      <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>
                        {donation.transactionId}
                      </TableCell>
                      <TableCell sx={{ width: 90, whiteSpace: "nowrap" }}>
                        {donation.bookNo}
                      </TableCell>
                      <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                        {donation.displayReceiptNo}
                      </TableCell>
                      <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                        {donation.actualReceiptNo || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>
                        {donation.receiptDate
                          ? new Date(donation.receiptDate).toLocaleDateString()
                          : ""}
                      </TableCell>
                      <TableCell sx={{ width: 100, whiteSpace: "nowrap" }}>
                        {donation.amount}
                      </TableCell>
                      <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                        {donation.payMode}
                      </TableCell>
                      <TableCell sx={{ width: 150, whiteSpace: "nowrap" }}>
                        {donation.bankName || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>
                        {donation.instrumentNo || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 150, whiteSpace: "nowrap" }}>
                        {donation.instrumentDate
                          ? new Date(
                              donation.instrumentDate
                            ).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ width: 160, whiteSpace: "nowrap" }}>{donation.upiId || "-"}</TableCell>
                      <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>{donation.payPurpose}</TableCell>
                      <TableCell sx={{ width: 180, whiteSpace: "nowrap" }}>{donation.tributeName || "-"}</TableCell>
                      <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>{donation.sharma || "-"}</TableCell>
                      <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>{donation.poojaDate || "-"}</TableCell>
                      <TableCell sx={{ width: 160, whiteSpace: "nowrap" }}>{donation.tamilMonths || "-"}</TableCell>
                      <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>{donation.nachathiram || "-"}</TableCell>
                      <TableCell sx={{ width: 120, whiteSpace: "nowrap" }}>{donation.thithi || "-"}</TableCell>
                      <TableCell sx={{ width: 120, whiteSpace: "nowrap" }}>{donation.utsavam || "-"}</TableCell>
                      <TableCell sx={{ width: 180, whiteSpace: "nowrap" }}>{donation.donationRemarks || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default Row;
